import cardBase from '../data/cardBase.json'
import locationBase from '../data/locationBase.json'
import titleBase from '../data/titleBase.json'
import titleBaseEN from '../data/titleBaseEN.json'
import { safeReadJson, safeWriteJson, safeRemove } from './storage'

const KEYS = {
  cards: 'cards_db_v1',
  locations: 'locations_db_v1',
  titles: 'titles_db_v1',
}

const EVENTS = {
  cards: 'cards-db-updated',
  locations: 'locations-db-updated',
  titles: 'titles-db-updated',
}

const emit = (eventName) => {
  window.dispatchEvent(new Event(eventName))
}

const uniqStrings = (items) => {
  const source = Array.isArray(items) ? items : []
  return source
    .map((item) => String(item).trim())
    .filter((item, index, arr) => item && arr.indexOf(item) === index)
}

const normalizeCard = (item) => ({
  name: String(item.name || '').trim(),
  nameEN: String(item.nameEN || '').trim(),
  descr: String(item.descr || '').trim(),
  descrEN: String(item.descrEN || '').trim(),
  cost: String(item.cost ?? '').trim(),
  power: String(item.power ?? '').trim(),
  category: String(item.category || 'Not Available').trim(),
  status: String(item.status || 'released').trim(),
  type: Array.isArray(item.type)
    ? uniqStrings(item.type)
    : uniqStrings(String(item.type || '').split(',')),
  imgUrl: String(item.imgUrl || '').trim(),
  nameId: String(item.nameId || '000000000000000000').trim(),
  descrId: String(item.descrId || '000000000000000000').trim(),
})

const normalizeLocation = (item) => ({
  name: String(item.name || '').trim(),
  nameEN: String(item.nameEN || '').trim(),
  descr: String(item.descr || '').trim(),
  descrEN: String(item.descrEN || '').trim(),
  rarity: String(item.rarity || 'common').trim(),
  status: String(item.status || 'released').trim(),
  imgUrl: String(item.imgUrl || '').trim(),
  nameId: String(item.nameId || '000000000000000000').trim(),
  descrId: String(item.descrId || '000000000000000000').trim(),
})

const normalizeCards = (items) => {
  const source = Array.isArray(items) ? items : []
  return source.map(normalizeCard).filter((item) => item.name && item.nameEN)
}

const normalizeLocations = (items) => {
  const source = Array.isArray(items) ? items : []
  return source
    .map(normalizeLocation)
    .filter((item) => item.name && item.nameEN)
}

const normalizeTitlesDb = (db) => {
  const source = db && typeof db === 'object' ? db : {}
  const ru = uniqStrings(source.RU || source.ru || titleBase)
  const en = uniqStrings(source.EN || source.en || titleBaseEN)

  return { RU: ru, EN: en }
}

const subscribe = (eventName, callback, getter) => {
  const handler = () => callback(getter())
  window.addEventListener(eventName, handler)
  window.addEventListener('storage', handler)

  return () => {
    window.removeEventListener(eventName, handler)
    window.removeEventListener('storage', handler)
  }
}

export const getBaseCards = () => normalizeCards(cardBase)
export const getBaseLocations = () => normalizeLocations(locationBase)
export const getBaseTitlesDb = () => normalizeTitlesDb({ RU: titleBase, EN: titleBaseEN })

export const getCards = () => {
  const stored = safeReadJson(KEYS.cards, null)
  if (Array.isArray(stored) && stored.length > 0) return normalizeCards(stored)
  return getBaseCards()
}

export const getLocations = () => {
  const stored = safeReadJson(KEYS.locations, null)
  if (Array.isArray(stored) && stored.length > 0) return normalizeLocations(stored)
  return getBaseLocations()
}

export const getTitlesDb = () => {
  const stored = safeReadJson(KEYS.titles, null)
  if (stored && typeof stored === 'object') return normalizeTitlesDb(stored)
  return getBaseTitlesDb()
}

export const saveCards = (items) => {
  const normalized = normalizeCards(items)
  safeWriteJson(KEYS.cards, normalized)
  emit(EVENTS.cards)
  return normalized
}

export const saveLocations = (items) => {
  const normalized = normalizeLocations(items)
  safeWriteJson(KEYS.locations, normalized)
  emit(EVENTS.locations)
  return normalized
}

export const saveTitlesDb = (db) => {
  const normalized = normalizeTitlesDb(db)
  safeWriteJson(KEYS.titles, normalized)
  emit(EVENTS.titles)
  return normalized
}

export const resetCards = () => {
  safeRemove(KEYS.cards)
  emit(EVENTS.cards)
}

export const resetLocations = () => {
  safeRemove(KEYS.locations)
  emit(EVENTS.locations)
}

export const resetTitles = () => {
  safeRemove(KEYS.titles)
  emit(EVENTS.titles)
}

export const onCardsUpdated = (callback) => subscribe(EVENTS.cards, callback, getCards)
export const onLocationsUpdated = (callback) => subscribe(EVENTS.locations, callback, getLocations)
export const onTitlesUpdated = (callback) => subscribe(EVENTS.titles, callback, getTitlesDb)
